<template>
    <div class="p-4 bg-white shadow-md rounded">
        <div class="relative p-2 border rounded" v-bind="getRootProps()">
            <img class="w-full h-auto rounded" v-if="imagePreviewUrl" :src="imagePreviewUrl" alt="Uploaded image" />
            <span
                class="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-full">
                <i class="fas fa-pen text-gray-400"></i>
                <input v-bind="getInputProps()" type="file" name="profile_avatar" @change="handleImageChange"
                    class="hidden" />
            </span>
            <span v-if="imagePreviewUrl !== 'media/avatars/blank2.png'"
                class="absolute top-8 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 border border-red-600 rounded text-white fas fa-trash-alt"
                @click="removeImage">
                <input type="file" name="profile_avatar" ref="fileInput" class="hidden" />
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, PropType } from "vue";
import { useDropzone } from "vue3-dropzone";
import ApiService from "@/_services/api.service";
import { IVignette } from "@/models/vignette/interface/IVignette";
const props = defineProps({
    imagePreviewUrl: {
        type: null as unknown as PropType<string | null>,
    },
    acceptedExtensions:{
        type: Array,
    },
});
const imagePreviewUrl = ref(props.imagePreviewUrl);
const acceptedExtensions =  props.acceptedExtensions?.length ? props.acceptedExtensions: [".jpg", ".jpeg", ".svg", ".png"];
const maxSizeAllowed = 2000000;
const acceptedMimeTypes = [
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
    "image/png",
];
const wrongFormatErrorMsg =
    "Le format de fichier doit être en JPG, PNG ou SVG.";
const tooLargeFileErrorMsg =
    "Le fichier est trop volumineux. Sa taille ne doit pas dépasser 2 Mo.";
const globalErrorMsg = "Désolé, il y a eu un problème au téléchargement de l'image. Veuillez réessayer. 5";
const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxSize: maxSizeAllowed,
    accept: acceptedExtensions.join(", "),
    onDropRejected: (rejectReasons: object) => {
        let errorCode = rejectReasons[0].errors[0].code;
        if (errorCode === "file-too-large") {
            emit("error", tooLargeFileErrorMsg);
            return;
        }
        if (errorCode === "file-invalid-type") {
            emit("error", wrongFormatErrorMsg);
            return;
        }
        emit("error", globalErrorMsg);
    },
});

const emit = defineEmits<{
    (e: "imageUploaded", value: IVignette): IVignette;
    (e: "imageRemoved", value: null): null;
    (e: "error", value: string): string;
}>();
const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && !acceptedMimeTypes.includes(file.type)) {
        emit("error", wrongFormatErrorMsg);
    }
    if (file && acceptedMimeTypes.includes(file.type)) {
        onDrop(e.target.files);
    }
};
async function onDrop(acceptFiles) {
    try {
        if (acceptFiles.length > 0 && acceptFiles instanceof FileList) {
            let fileToSave = acceptFiles[0];
            if (fileToSave.size > maxSizeAllowed) {
                emit("error", tooLargeFileErrorMsg);
            } else {
                let file = new FormData();
                file.append("file", fileToSave);
                await ApiService.post("images", file).then((response) => {
                    imagePreviewUrl.value = response.data.data.url;
                    emit("imageUploaded", response.data.data);
                });
            }
        }
    } catch (error) {
        emit("error", globalErrorMsg);
    }
}

watch(
    () => props.imagePreviewUrl,
    (newValue) => {
        if (newValue !== undefined && newValue !== null) {
            imagePreviewUrl.value = newValue as string;
        } else {
            imagePreviewUrl.value = "../../assets/img/blank2.png";
        }
    }
);
const removeImage = () => {
    imagePreviewUrl.value = "../../assets/img/blank2.png";
    emit("imageRemoved", null);
};
</script>  